import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, QrCode, Copy, CheckCircle, Clock, ArrowLeft } from 'lucide-react';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
// import Barcode from 'react-barcode'; // Desabilitado - Boleto removido
// import { FileText } from 'lucide-react'; // Desabilitado - Boleto removido

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = () => {
  const { workId, planId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [pixData, setPixData] = useState(null);
  const [boletoData, setBoletoData] = useState(null); // Re-habilitado para funcionalidade completa
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [plan, setPlan] = useState(planId || 'individual');
  const [dynamicPlans, setDynamicPlans] = useState(null);

  // Função segura para copiar texto com fallback
  const copyToClipboard = async (text, successMessage = 'Copiado!') => {
    try {
      // Tenta usar a API moderna de Clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(successMessage);
        return true;
      }
      
      // Fallback: método antigo usando textarea temporário
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        textArea.remove();
        if (successful) {
          toast.success(successMessage);
          return true;
        } else {
          throw new Error('Copy command failed');
        }
      } catch (err) {
        textArea.remove();
        throw err;
      }
    } catch (err) {
      console.error('Erro ao copiar:', err);
      // Mostra o texto para copiar manualmente
      toast.error('Não foi possível copiar automaticamente. Código: ' + text.substring(0, 20) + '...');
      return false;
    }
  };

  // Carregar preços dinâmicos
  useEffect(() => {
    loadDynamicPrices();
    
    // Registrar listener para atualizações em tempo real
    import('../services/plansSyncService').then(({ default: plansSyncService }) => {
      const unsubscribe = plansSyncService.addListener((plansConfig) => {
        console.log('🔄 PaymentPage: Atualizando preços...');
        setDynamicPlans(plansConfig);
      });
      
      return () => unsubscribe();
    });
  }, []);

  const loadDynamicPrices = async () => {
    try {
      console.log('📥 PaymentPage: Carregando planos dinâmicos...');
      const response = await axios.get(`${API}/plans/config`);
      console.log('✅ PaymentPage: Planos recebidos:', response.data);
      
      if (response.data.success && response.data.plans) {
        console.log('✅ PaymentPage: Setando dynamicPlans:');
        console.log('   Individual:', response.data.plans.individual?.duration_months, 'meses');
        console.log('   Professional:', response.data.plans.professional?.duration_months, 'meses');
        console.log('   Premium:', response.data.plans.premium?.duration_months, 'meses');
        console.log('   Annual:', response.data.plans.annual?.duration_months, 'meses');
        
        setDynamicPlans(response.data.plans);
      }
    } catch (error) {
      console.error('❌ Error loading dynamic prices:', error);
    }
  };

  // Função para formatar preço em reais (R$ 99,90)
  const formatPrice = (price) => {
    return price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Planos com valores dinâmicos (fallback para valores padrão)
  const plans = {
    individual: { 
      name: dynamicPlans?.individual?.name || 'Gratuito', 
      price: dynamicPlans?.individual?.price || 0, 
      description: `Até ${dynamicPlans?.individual?.music_limit || 5} registros de obras - Totalmente Gratuito`,
      features: [`${dynamicPlans?.individual?.music_limit || 5} músicas`, 'Certificados digitais', 'Galeria pública']
    },
    professional: { 
      name: dynamicPlans?.professional?.name || '10 Músicas', 
      price: dynamicPlans?.professional?.price || 99.90, 
      description: `Até ${dynamicPlans?.professional?.music_limit || 10} registros de obras - ${dynamicPlans?.professional?.duration_months || 6} meses`,
      features: [`${dynamicPlans?.professional?.music_limit || 10} músicas`, 'Certificados ilimitados', 'Suporte básico', 'Galeria pública']
    },
    premium: { 
      name: dynamicPlans?.premium?.name || 'Plano SEMESTRAL', 
      price: dynamicPlans?.premium?.price || 299.90, 
      description: `Obras ${dynamicPlans?.premium?.music_limit === -1 ? 'ilimitadas' : dynamicPlans?.premium?.music_limit} por ${dynamicPlans?.premium?.duration_months || 6} meses`,
      features: [`Músicas ${dynamicPlans?.premium?.music_limit === -1 ? 'ilimitadas' : dynamicPlans?.premium?.music_limit} (${dynamicPlans?.premium?.duration_months || 6} meses)`, 'Certificados ilimitados', 'Suporte prioritário', 'Galeria em destaque']
    },
    annual: {
      name: dynamicPlans?.annual?.name || 'Plano ANUAL ILIMITADO',
      price: dynamicPlans?.annual?.price || 299.90,
      description: `Obras ilimitadas por ${dynamicPlans?.annual?.duration_months || 12} meses`,
      popular: true,
      features: dynamicPlans?.annual?.features || [
        `✨ Músicas ILIMITADAS (${dynamicPlans?.annual?.duration_months || 12} meses)`,
        '🎵 Áudios ilimitados',
        '📜 Certificados ilimitados',
        '⭐ Perfil verificado',
        '📊 Estatísticas exclusivas',
        '🎖️ Badge "Compositor Profissional"',
        '🔝 Destaque na galeria',
        '🎯 Suporte VIP prioritário',
        '🔗 URL personalizada'
      ]
    }
  };

  // Função para calcular CRC16-CCITT para payload Pix (Padrão BACEN)
  const calcularCRC16 = (str) => {
    let crc = 0xFFFF;
    for (let i = 0; i < str.length; i++) {
      crc ^= (str.charCodeAt(i) << 8);
      for (let j = 0; j < 8; j++) {
        if ((crc & 0x8000) !== 0) {
          crc = ((crc << 1) ^ 0x1021) & 0xFFFF;
        } else {
          crc = (crc << 1) & 0xFFFF;
        }
      }
    }
    return crc.toString(16).toUpperCase().padStart(4, '0');
  };

  // Função para gerar QR Code PIX válido (padrão EMV BACEN)
  const gerarPixQRCode = (valor) => {
    // Dados da conta conforme REGISTRO ATUAL no Banco Central
    const chavePix = '63854898000185'; // CNPJ sem pontuação
    const nomeRecebedor = 'MINHA COMPOSICAO AUTORAL'; // Nome do BC (25 chars máximo)
    const cidade = 'LAURO DE FREIT'; // 14 caracteres (máximo 15)
    const descricao = 'Pagamento'; // Descrição curta opcional
    
    // Gerar txid único (formato FAT2025-XXXXX)
    const timestamp = Date.now().toString().slice(-6);
    const txId = `FAT2025-${timestamp}`; // Max 25 chars
    
    // Construir payload PIX seguindo padrão EMV/BACEN
    let payload = '';
    
    // 00: Payload Format Indicator (sempre "01")
    payload += '000201';
    
    // 01: Point of Initiation Method
    // 11 = ESTÁTICO (QR reutilizável, recomendado)
    // 12 = DINÂMICO (QR único por transação)
    payload += '010211';
    
    // 26: Merchant Account Information (PIX)
    const gui = 'BR.GOV.BCB.PIX';
    let merchantAccount = `0014${gui}01${String(chavePix.length).padStart(2, '0')}${chavePix}`;
    
    // Adicionar descrição opcional (campo 02)
    if (descricao) {
      merchantAccount += `02${String(descricao.length).padStart(2, '0')}${descricao}`;
    }
    
    payload += `26${String(merchantAccount.length).padStart(2, '0')}${merchantAccount}`;
    
    // 52: Merchant Category Code (0000 = não especificado)
    payload += '52040000';
    
    // 53: Transaction Currency (986 = Real Brasileiro)
    payload += '5303986';
    
    // 54: Transaction Amount (OBRIGATÓRIO para boleto→Pix)
    if (valor && valor > 0) {
      const valorStr = valor.toFixed(2);
      payload += `54${String(valorStr.length).padStart(2, '0')}${valorStr}`;
    }
    
    // 58: Country Code (BR = Brasil)
    payload += '5802BR';
    
    // 59: Merchant Name (MEGA NET TECNOLOGIA DIG - nome ATUAL registrado no BC)
    payload += `59${String(nomeRecebedor.length).padStart(2, '0')}${nomeRecebedor}`;
    
    // 60: Merchant City (LAURO DE FREITAS)
    payload += `60${String(cidade.length).padStart(2, '0')}${cidade}`;
    
    // 62: Additional Data Field Template
    // ID 05: Reference Label (txid para conciliação)
    const referenceLabel = `05${String(txId.length).padStart(2, '0')}${txId}`;
    payload += `62${String(referenceLabel.length).padStart(2, '0')}${referenceLabel}`;
    
    // 63: CRC16-CCITT (calculado sobre payload + "6304")
    payload += '6304';
    const crc = calcularCRC16(payload);
    payload += crc;
    
    return payload;
  };

  // Update plan when planId changes
  React.useEffect(() => {
    if (planId && plans[planId]) {
      setPlan(planId);
    }
  }, [planId]);

  // Default to professional if plan is invalid
  const currentPlan = plans[plan] || plans['professional'];

  const handlePixPayment = async () => {
    setLoading(true);
    try {
      // Se for plano gratuito, pular pagamento
      if (plan === 'individual') {
        toast.success('Plano gratuito ativado!');
        if (workId) {
          navigate(`/works/${workId}`);
        } else {
          navigate('/dashboard');
        }
        return;
      }

      // ✅ CHAMAR API DO BACKEND PARA GERAR PIX E REGISTRAR TRANSAÇÃO
      const response = await axios.post(
        `${API}/payments/create-pix-transaction`,
        {
          work_id: workId || 'no-work',
          plan: plan
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Criar objeto com dados do PIX retornado pela API
        const pixDataReal = {
          qr_code: response.data.pix_code,
          payment_id: response.data.tx_id,  // ✅ TxID PIX EMV (formato: E003603052025...)
          transaction_code: response.data.transaction_code,  // Código interno (TRX-XXXXX)
          tx_id: response.data.tx_id,  // ✅ TxID PIX para exibir
          amount: response.data.amount,
          chave_pix: '63.854.898/0001-85',
          beneficiario: 'MINHA COMPOSIÇÃO AUTORAL'
        };

        setPixData(pixDataReal);
        toast.success('✅ PIX gerado com sucesso! Transação registrada.');
        
        // Instruções de pagamento com TxID PIX
        setTimeout(() => {
          toast.info(`📱 ID de transação Pix: ${response.data.tx_id}`, { duration: 8000 });
        }, 1500);
      } else {
        throw new Error('Falha ao gerar PIX');
      }

    } catch (error) {
      console.error('Erro ao gerar PIX:', error);
      toast.error('❌ Erro ao gerar PIX. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCardPayment = async () => {
    setLoading(true);
    try {
      // MOCK - Simulação de pagamento com cartão
      toast.success('Formulário validado! Dados enviados com sucesso.');
      
      // Aviso
      setTimeout(() => {
        toast.info('⚠️ MOCK: Envie print do formulário para sacminhascomposicoes@gmail.com', { duration: 8000 });
      }, 1500);

      // Redirecionar após 3 segundos
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);

    } catch (error) {
      console.error('Erro no pagamento:', error);
      toast.error('Erro ao processar pagamento');
    } finally {
      setTimeout(() => setLoading(false), 3000);
    }
  };

  const startPaymentPolling = (paymentId) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${API}/payments/status/${paymentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.status === 'approved') {
          clearInterval(interval);
          toast.success('Pagamento aprovado!');
          navigate(`/works/${workId}`);
        }
      } catch (error) {
        console.error('Erro ao verificar pagamento:', error);
      }
    }, 3000); // Verificar a cada 3 segundos

    // Limpar após 10 minutos
    setTimeout(() => clearInterval(interval), 600000);
  };

  const copyPixCode = () => {
    if (pixData?.qr_code) {
      copyToClipboard(pixData.qr_code, 'Código PIX copiado!');
    }
  };

  return (
    <>
      <style>{`
        @media print {
          /* Esconde tudo primeiro */
          * {
            visibility: hidden;
          }
          
          /* Mostra apenas o boleto e seus filhos */
          #boleto-print-area,
          #boleto-print-area * {
            visibility: visible;
          }
          
          /* Posiciona o boleto no topo da página */
          #boleto-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          
          /* Remove margens */
          @page {
            margin: 1cm;
          }
        }
      `}</style>
      <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* Botão Voltar */}
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-6 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Voltar</span>
          </button>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Finalizar Pagamento</h1>
          <p className="text-gray-600 mb-8">
            Escolha a forma de pagamento para registrar sua obra
          </p>

          {/* Banner PIX */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="bg-green-100 p-3 rounded-xl">
                <QrCode className="w-8 h-8 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-green-900 mb-2">
                  💳 Chave PIX Cadastrada
                </h3>
                <div className="space-y-1 text-sm text-green-800">
                  <p><strong>Tipo:</strong> CNPJ</p>
                  <p><strong>Chave:</strong> 63.854.898/0001-85</p>
                  <p><strong>Beneficiário:</strong> MINHA COMPOSIÇÃO AUTORAL</p>
                </div>
                <p className="text-xs text-green-700 mt-3 bg-green-100 p-2 rounded-lg">
                  ✅ Pagamentos PIX são confirmados instantaneamente!
                </p>
              </div>
            </div>
          </div>

          {/* Seleção de Plano */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Selecione o Plano</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(plans).map(([key, planData]) => (
                <button
                  key={key}
                  onClick={() => setPlan(key)}
                  className={`p-4 border-2 rounded-xl transition-all ${
                    plan === key
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <h4 className="font-bold text-gray-900 mb-1">{planData.name}</h4>
                  <p className="text-2xl font-bold text-blue-600 mb-1">
                    R$ {formatPrice(planData.price)}
                  </p>
                  <p className="text-sm text-gray-600">{planData.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Método de Pagamento */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Forma de Pagamento</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <button
                onClick={() => setPaymentMethod('pix')}
                className={`p-6 border-2 rounded-xl flex items-center space-x-4 transition-all ${
                  paymentMethod === 'pix'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <QrCode className="w-8 h-8 text-blue-600" />
                <div className="text-left">
                  <h4 className="font-bold text-gray-900">PIX</h4>
                  <p className="text-sm text-gray-600">Aprovação instantânea</p>
                </div>
              </button>

              {/*               <button*/}
              {/*                 onClick={() => setPaymentMethod('card')}*/}
              {/*                 className={`p-6 border-2 rounded-xl flex items-center space-x-4 transition-all ${*/}
          {/* CARTÃO DESABILITADO
          */}
              {/*                     : 'border-gray-200 hover:border-blue-300'*/}
              {/*                 }`}*/}
              {/*               >*/}
              {/*                 <CreditCard className="w-8 h-8 text-green-600" />*/}
              {/*                 <div className="text-left">*/}
              {/*                   <h4 className="font-bold text-gray-900">Cartão de Crédito</h4>*/}
              {/*                   <p className="text-sm text-gray-600">Até 12x sem juros</p>*/}
              {/*                 </div>*/}
              {/*               </button>*/}
              {/* */}
              {/*               {/* Boleto Bancário - DESABILITADO */}*/}
              {/* 
              <button
                onClick={() => setPaymentMethod('boleto')}
                className={`p-6 border-2 rounded-xl flex items-center space-x-4 transition-all ${
                  paymentMethod === 'boleto'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <FileText className="w-8 h-8 text-orange-600" />
                <div className="text-left">
                  <h4 className="font-bold text-gray-900">Boleto Bancário</h4>
                  <p className="text-sm text-gray-600">Vencimento em 3 dias</p>
                </div>
              </button>
              */}
            </div>
          </div>

          {/* PIX Payment */}
          {paymentMethod === 'pix' && !pixData && (
            <button
              onClick={handlePixPayment}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Processando...' : (plan === 'individual' ? 'Confirmar Registro Gratuito' : 'Gerar QR Code PIX')}
            </button>
          )}

          {/* PIX QR Code */}
          {pixData && (
            <div className="border-2 border-blue-200 rounded-2xl p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <QrCode className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  PIX Gerado com Sucesso!
                </h3>
                <p className="text-gray-600">
                  Pague com o app do seu banco em segundos
                </p>
              </div>

              {/* Valor */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-xl p-4 mb-6 text-center">
                <p className="text-sm text-gray-600 mb-1">Valor a Pagar:</p>
                <p className="text-3xl font-bold text-blue-600">
                  R$ {formatPrice(pixData.amount)}
                </p>
              </div>

              {/* QR Code */}
              <div className="text-center mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">
                  📱 Opção 1: Escaneie o QR Code
                </p>
                <div className="bg-white p-6 rounded-xl inline-block shadow-lg border-2 border-gray-200">
                  <QRCode value={pixData.qr_code} size={240} level="H" includeMargin />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Abra o app do seu banco e escaneie este código
                </p>
              </div>

              {/* PIX Copia e Cola */}
              <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-4 mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center justify-center">
                  <Copy className="w-4 h-4 mr-2" />
                  Opção 2: PIX Copia e Cola
                </p>
                <div className="bg-white border border-gray-300 rounded-lg p-3 mb-3">
                  <p className="font-mono text-xs text-gray-900 break-all max-h-24 overflow-y-auto">
                    {pixData.qr_code}
                  </p>
                </div>
                <button
                  onClick={copyPixCode}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center justify-center space-x-2"
                >
                  <Copy className="w-5 h-5" />
                  <span>Copiar Código PIX</span>
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Cole este código no app do seu banco na opção "Pagar com PIX"
                </p>
              </div>

              {/* Dados do Beneficiário */}
              <div className="bg-green-50 border border-green-300 rounded-xl p-4 mb-6">
                <p className="text-sm font-semibold text-green-800 mb-2">
                  💰 Dados do Beneficiário:
                </p>
                <div className="space-y-1 text-sm text-green-800">
                  <p><strong>Nome:</strong> MINHA COMPOSIÇÃO AUTORAL</p>
                  <p><strong>CNPJ:</strong> 63.854.898/0001-85</p>
                  <p><strong>Chave PIX:</strong> 63.854.898/0001-85 (CNPJ)</p>
                </div>
              </div>

              {/* Instruções */}
              <div className="bg-blue-50 border border-blue-300 rounded-xl p-4 mb-6">
                <p className="text-sm font-semibold text-blue-800 mb-2">
                  ℹ️ Como Pagar:
                </p>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Abra o app do seu banco</li>
                  <li>Escolha "Pagar com PIX"</li>
                  <li>Escaneie o QR Code OU cole o código acima</li>
                  <li>Confirme o valor de <strong>R$ {formatPrice(pixData.amount)}</strong></li>
                  <li>Finalize o pagamento</li>
                </ol>
              </div>

              {/* Aviso */}
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 text-center">
                <p className="text-sm text-yellow-800">
                  <strong>⏱️ Pagamento PIX:</strong> A confirmação é instantânea!
                  <br />
                  Após pagar, envie o comprovante para: <strong>sacminhascomposicoes@gmail.com</strong>
                </p>
              </div>
            </div>
          )}

          {/* Bank Transfer */}
          {paymentMethod === 'transfer' && (
            <div className="border-2 border-green-200 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Dados para Transferência Bancária
              </h3>
              
              <div className="space-y-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-sm text-gray-600 mb-1">Banco:</p>
                  <p className="text-lg font-bold text-gray-900">Santander (033)</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Agência:</p>
                    <p className="text-lg font-bold text-gray-900">0933</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Conta Corrente:</p>
                    <p className="text-lg font-bold text-gray-900">13002933-2</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-sm text-gray-600 mb-1">Favorecido:</p>
                  <p className="text-lg font-bold text-gray-900">MINHA COMPOSIÇÃO AUTORAL</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-sm text-gray-600 mb-1">CNPJ:</p>
                  <p className="text-lg font-bold text-gray-900">63.854.898/0001-85</p>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-xl border-2 border-green-300">
                  <p className="text-sm text-gray-600 mb-1">PIX (CNPJ):</p>
                  <p className="text-xl font-bold text-green-700">63.854.898/0001-85</p>
                  <button
                    onClick={() => copyToClipboard('63.854.898/0001-85', 'Chave PIX copiada!')}
                    className="mt-2 bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors inline-flex items-center space-x-2 text-sm"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copiar Chave PIX</span>
                  </button>
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-sm text-gray-600 mb-1">Valor a Transferir:</p>
                  <p className="text-3xl font-bold text-blue-600">
                    R$ {currentPlan.price.toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Importante:</strong> Após realizar a transferência, envie o comprovante 
                  para <strong>sacminhascomposicoes@gmail.com</strong> com o título da obra. 
                  Seu certificado será liberado em até 24 horas úteis.
                </p>
              </div>

              <button
                onClick={() => {
                  toast.success('Instruções salvas! Aguardamos seu comprovante.');
                  setTimeout(() => navigate('/dashboard'), 2000);
                }}
                className="w-full bg-green-600 text-white py-4 rounded-xl font-semibold hover:bg-green-700 transition-colors"
              >
                Confirmar e Voltar ao Dashboard
              </button>
            </div>
          )}

          {/* Card Payment Form */}
          {false && paymentMethod === 'card' && (
            <div className="border-2 border-green-200 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Pagamento com Cartão de Crédito
              </h3>
              
              <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleCardPayment(); }}>
                {/* Número do Cartão */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número do Cartão *
                  </label>
                  <input
                    type="text"
                    placeholder="0000 0000 0000 0000"
                    maxLength="19"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onChange={(e) => {
                      // Formatar com espaços a cada 4 dígitos
                      let value = e.target.value.replace(/\s/g, '');
                      let formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                      e.target.value = formatted;
                    }}
                    required
                  />
                </div>

                {/* Nome no Cartão */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome impresso no Cartão *
                  </label>
                  <input
                    type="text"
                    placeholder="NOME COMPLETO"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                    required
                  />
                </div>

                {/* Validade e CVV */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Validade *
                    </label>
                    <input
                      type="text"
                      placeholder="MM/AA"
                      maxLength="5"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length >= 2) {
                          value = value.slice(0, 2) + '/' + value.slice(2, 4);
                        }
                        e.target.value = value;
                      }}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CVV *
                    </label>
                    <input
                      type="text"
                      placeholder="000"
                      maxLength="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      onChange={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, '');
                      }}
                      required
                    />
                  </div>
                </div>

                {/* CPF do Titular */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CPF do Titular *
                  </label>
                  <input
                    type="text"
                    placeholder="000.000.000-00"
                    maxLength="14"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 11) {
                        value = value.replace(/(\d{3})(\d)/, '$1.$2');
                        value = value.replace(/(\d{3})(\d)/, '$1.$2');
                        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                      }
                      e.target.value = value;
                    }}
                    required
                  />
                </div>

                {/* Parcelas */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parcelas *
                  </label>
                  <select 
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="1">1x de R$ {currentPlan.price.toFixed(2)} sem juros</option>
                    <option value="2">2x de R$ {(currentPlan.price / 2).toFixed(2)} sem juros</option>
                    <option value="3">3x de R$ {(currentPlan.price / 3).toFixed(2)} sem juros</option>
                    <option value="4">4x de R$ {(currentPlan.price / 4).toFixed(2)} sem juros</option>
                    <option value="5">5x de R$ {(currentPlan.price / 5).toFixed(2)} sem juros</option>
                    <option value="6">6x de R$ {(currentPlan.price / 6).toFixed(2)} sem juros</option>
                    <option value="7">7x de R$ {(currentPlan.price / 7).toFixed(2)} sem juros</option>
                    <option value="8">8x de R$ {(currentPlan.price / 8).toFixed(2)} sem juros</option>
                    <option value="9">9x de R$ {(currentPlan.price / 9).toFixed(2)} sem juros</option>
                    <option value="10">10x de R$ {(currentPlan.price / 10).toFixed(2)} sem juros</option>
                    <option value="11">11x de R$ {(currentPlan.price / 11).toFixed(2)} sem juros</option>
                    <option value="12">12x de R$ {(currentPlan.price / 12).toFixed(2)} sem juros</option>
                  </select>
                </div>

                {/* Valor Total */}
                <div className="bg-blue-50 p-4 rounded-xl">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Total a pagar:</span>
                    <span className="text-3xl font-bold text-blue-600">R$ {currentPlan.price.toFixed(2)}</span>
                  </div>
                </div>

                {/* Aviso MOCK */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ MOCK:</strong> Sistema de pagamento em configuração. 
                    Após preencher, envie print deste formulário para <strong>sacminhascomposicoes@gmail.com</strong> 
                    que liberaremos seu acesso manualmente.
                  </p>
                </div>

                {/* Botão de Pagamento */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-green-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processando...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Confirmar Pagamento
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Boleto Bancário - DESABILITADO */}
          {false && paymentMethod === 'boleto' && !boletoData && (
            <div className="border-2 border-orange-200 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Gerar Boleto Bancário
              </h3>
              
              <div className="space-y-4 mb-6">
                <div className="bg-orange-50 p-4 rounded-xl">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Valor do Boleto:</strong> R$ {currentPlan.price.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Vencimento:</strong> {new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR')} (3 dias úteis)
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Beneficiário:</strong> MINHA COMPOSIÇÃO AUTORAL
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  const pixQRCode = gerarPixQRCode(currentPlan.price);
                  const boleto = {
                    linha_digitavel: `34191.79001 01043.510047 91020.150008 ${Math.floor(Math.random() * 10)} ${(Math.random() * 100000000000000).toFixed(0)}`,
                    codigo_barras: `34191${Math.floor(Math.random() * 10)}${(Math.random() * 10000000000000000000000000000000000000000).toFixed(0)}`,
                    vencimento: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR'),
                    valor: currentPlan.price,
                    pix_qrcode: pixQRCode
                  };
                  setBoletoData(boleto);
                  toast.success('Boleto gerado com sucesso! QR Code Pix vinculado ao CNPJ 63.854.898/0001-85');
                }}
                className="w-full bg-orange-600 text-white py-4 rounded-xl font-bold hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Gerar Boleto
              </button>
            </div>
          )}

          {/* Boleto Gerado - DESABILITADO */}
          {false && paymentMethod === 'boleto' && boletoData && (
            <div id="boleto-print-area" className="border-2 border-orange-200 rounded-2xl p-8">
              <div className="text-center mb-6 no-print">
                <FileText className="w-16 h-16 text-orange-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Boleto Gerado com Sucesso!
                </h3>
                <p className="text-gray-600">
                  Pague até o vencimento para confirmar seu plano
                </p>
              </div>

              {/* Dados do Boleto */}
              <div className="bg-white border-2 border-orange-300 rounded-xl p-6 mb-6">
                <div className="space-y-4">
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-sm text-gray-600">Beneficiário:</span>
                    <span className="font-semibold">MINHA COMPOSIÇÃO AUTORAL</span>
                  </div>
                  
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-sm text-gray-600">CNPJ:</span>
                    <span className="font-semibold">63.854.898/0001-85</span>
                  </div>
                  
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-sm text-gray-600">Vencimento:</span>
                    <span className="font-semibold text-orange-600">{boletoData.vencimento}</span>
                  </div>
                  
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-sm text-gray-600">Valor:</span>
                    <span className="font-bold text-2xl text-orange-600">R$ {boletoData.valor.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Linha Digitável */}
              <div className="bg-gray-50 border border-gray-300 rounded-xl p-4 mb-4">
                <p className="text-xs text-gray-600 mb-2">Linha Digitável:</p>
                <p className="font-mono text-sm text-gray-900 break-all mb-3">
                  {boletoData.linha_digitavel}
                </p>
                <button
                  onClick={() => copyToClipboard(boletoData.linha_digitavel, 'Linha digitável copiada!')}
                  className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-orange-700 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copiar Linha Digitável
                </button>
              </div>

              {/* Código de Barras Visual */}
              <div className="bg-white border border-gray-300 rounded-xl p-4 mb-4">
                <p className="text-xs text-gray-600 mb-3 text-center">Código de Barras:</p>
                <div className="flex justify-center mb-2">
                  <Barcode 
                    value={boletoData.codigo_barras} 
                    format="CODE128"
                    width={2}
                    height={80}
                    displayValue={true}
                    fontSize={12}
                    margin={0}
                  />
                </div>
              </div>

              {/* QR Code PIX para Boleto */}
              <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-300 rounded-xl p-6 mb-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <QrCode className="w-6 h-6 text-green-600" />
                  <p className="text-lg text-gray-800 font-bold">Pague com PIX - Compensação Imediata</p>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-xl border-2 border-green-400 shadow-lg mb-4">
                    <QRCode 
                      value={boletoData.pix_qrcode}
                      size={200}
                      level="H"
                    />
                  </div>
                  
                  <div className="bg-white border border-green-200 rounded-lg p-4 mb-4 w-full">
                    <p className="text-sm text-gray-700 mb-2 text-center">
                      <strong>💰 Valor:</strong> <span className="text-green-600 text-xl font-bold">R$ {boletoData.valor.toFixed(2)}</span>
                    </p>
                    <p className="text-xs text-gray-600 text-center mb-2">
                      <strong>🏦 Beneficiário:</strong> MINHA COMPOSIÇÃO AUTORAL
                    </p>
                    <p className="text-xs text-gray-500 text-center italic mb-2">
                      (Razão social atual no Banco Central)
                    </p>
                    <p className="text-xs text-gray-600 text-center">
                      <strong>🔑 Chave Pix:</strong> CNPJ 63.854.898/0001-85
                    </p>
                  </div>
                  
                  <button
                    onClick={() => copyToClipboard(boletoData.pix_qrcode, 'Código PIX copiado! Cole no app do seu banco para pagar.')}
                    className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:from-green-700 hover:to-blue-700 transition-all shadow-md inline-flex items-center justify-center gap-2"
                  >
                    <Copy className="w-5 h-5" />
                    Copiar Código PIX
                  </button>
                  
                  <div className="mt-4 bg-green-100 border border-green-300 rounded-lg p-3 w-full">
                    <p className="text-xs text-green-800 font-semibold mb-2">✅ Como pagar com PIX:</p>
                    <ol className="text-xs text-green-800 space-y-1 list-decimal list-inside">
                      <li>Escaneie o QR Code acima com seu app bancário</li>
                      <li>Ou copie o código PIX e cole no app</li>
                      <li>Confirme o valor: R$ {boletoData.valor.toFixed(2)}</li>
                      <li>Finalize o pagamento - compensação imediata!</li>
                    </ol>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    ⚡ Pagamento via PIX é compensado instantaneamente<br/>
                    🔒 Chave Pix vinculada ao CNPJ oficial da empresa
                  </p>
                </div>
              </div>

              {/* Instruções */}
              <div className="bg-blue-50 border border-blue-300 rounded-xl p-4 mb-4">
                <p className="text-sm text-blue-800 font-semibold mb-2">📋 Formas de Pagamento:</p>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                    <p className="text-sm font-bold text-green-700 mb-1">🚀 RECOMENDADO: PIX (Compensação Imediata)</p>
                    <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside ml-2">
                      <li>Escaneie o QR Code ou copie o código PIX acima</li>
                      <li>Pagamento compensado instantaneamente</li>
                      <li>Chave Pix vinculada ao CNPJ 63.854.898/0001-85</li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-yellow-200">
                    <p className="text-sm font-bold text-orange-700 mb-1">📄 Boleto Tradicional (3 dias úteis)</p>
                    <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside ml-2">
                      <li>Copie a linha digitável</li>
                      <li>Pague pelo app do banco ou caixa eletrônico</li>
                      <li>Compensação em até 3 dias úteis</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Aviso MOCK */}
              <div className="bg-orange-50 border border-orange-300 rounded-xl p-4 mb-4">
                <p className="text-sm text-orange-800">
                  <strong>⚠️ IMPORTANTE:</strong> Após efetuar o pagamento via PIX ou boleto, 
                  envie o comprovante para <strong>sacminhascomposicoes@gmail.com</strong> 
                  que liberaremos seu acesso em até 24h (PIX: liberação mais rápida).
                </p>
              </div>

              {/* Botões */}
              <div className="grid grid-cols-2 gap-4 no-print">
                <button
                  onClick={() => {
                    // Abre boleto PDF em nova aba (igual certificado)
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
                    const params = new URLSearchParams({
                      linha_digitavel: boletoData.linha_digitavel,
                      codigo_barras: boletoData.codigo_barras,
                      vencimento: boletoData.vencimento,
                      valor: boletoData.valor.toString(),
                      plano: currentPlan.name
                    });
                    
                    const boletoUrl = `${backendUrl}/api/payment/boleto/pdf?${params.toString()}`;
                    const newWindow = window.open(boletoUrl, '_blank');
                    
                    if (newWindow) {
                      toast.success('Boleto aberto! Use Ctrl+P (ou Cmd+P no Mac) para imprimir ou clique no botão de download para salvar.', {
                        duration: 6000
                      });
                    } else {
                      toast.error('Popup bloqueado! Permita popups e tente novamente.');
                    }
                  }}
                  className="bg-gray-600 text-white py-3 rounded-xl font-semibold hover:bg-gray-700 transition-colors"
                >
                  🖨️ Imprimir Boleto
                </button>
                
                <button
                  onClick={() => {
                    toast.success('Boleto salvo! Envie comprovante após pagamento.');
                    setTimeout(() => navigate('/dashboard'), 2000);
                  }}
                  className="bg-orange-600 text-white py-3 rounded-xl font-semibold hover:bg-orange-700 transition-colors"
                >
                  Concluir
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default PaymentPage;
